/**
 * Manpower Configuration
 * 
 * Labor rates, workforce availability, and work type mappings
 */

// Labor daily rates (₹ per day) - configurable per project
export const DEFAULT_LABOR_RATES: Record<string, number> = {
  Mason: 800,
  Helper: 500,
  Carpenter: 900,
  Electrician: 950,
  Plumber: 850,
  Painter: 750,
  Fitter: 900
};

// Available workforce - configurable per project
export const DEFAULT_WORKFORCE: Record<string, number> = {
  Mason: 4,
  Helper: 8,
  Carpenter: 3,
  Electrician: 3,
  Plumber: 2,
  Painter: 3,
  Fitter: 2
};

/**
 * Work Type Mapping
 * Maps task subCategory to manpower constant work types
 */
export const WORK_TYPE_MAPPING: Record<string, string> = {
  // Masonry
  '100mm Block Work': '100 mm Thk Block Masonary',
  '150mm Block Work': '150 mm Thk Block Masonary',
  '200mm Block Work': '200 mm Thk Block Masonary',
  'Ledge Wall Block Work': '100 mm Thk Block Masonary', // Use 100mm as default
  'Coping Work': '100 mm Thk Block Masonary', // Approximate
  'DPC Work': '100 mm Thk Block Masonary', // Approximate
  '100mm Solid Block Work': '100 mm Thk Block Masonary',
  
  // Flooring & Tiling
  'Toilet Dado': 'Toilet Dado',
  'Common Toilet Dado': 'Toilet Dado',
  'Master Toilet 1 Dado': 'Toilet Dado',
  'Master Toilet 2 Dado': 'Toilet Dado',
  'Room Flooring': 'Room Flooring',
  'Toilet Flooring': 'Toilet Flooring',
  'Kitchen Dado': 'Kitchen & Wash Basin dado',
  'Utility Dado': 'Utility Dado',
  'Utility Flooring': 'Utility Flooring',
  'Window Sill': 'Window Sill',
  'Room Skirting': 'Room Skirting',
  
  // RCC
  'Concrete': 'Concrete',
  'Shuttering Work': 'Shuttering Work',
  'Reinforcement Work': 'Reinforcement Work',
  'Curing Compound Work': 'Concrete', // Approximate
  'Hole Filling': 'Concrete', // Approximate
  
  // Electrical
  'Slab Conducting & Wall Piping': 'Slab Conducting',
  'Slab Conducting': 'Slab Conducting',
  'Wall Piping': 'Wall Piping',
  'Wiring': 'Wiring',
  'Switches & Access': 'Switches & Access',
  
  // Plumbing
  'Internal Piping': 'Inter.CPVC',
  'CP & Sanitary': 'CP & Sanitary',
  'Inter.CPVC': 'Inter.CPVC',
  'Inter.PVC': 'Inter.PVC',
  
  // Painting
  'Wall Painting': 'Internal Painting 2nd/Final Coat',
  'Internal Painting': 'Internal Painting 2nd/Final Coat',
  'External Painting': 'External Painting',
  'Door Polish': 'Main Door Polish',
  'Main Door Polish': 'Main Door Polish',
  'Internal Putty': 'Internal 1st Coat Putty',
  
  // Doors & Windows
  'Main Door Frame': 'Main Door Frame',
  'Main Door Shutter': 'Main Door Shutter',
  'Bedroom Door Frame': 'Bedroom Door Frame',
  'Bedroom Door Shutter': 'Bedroom Door Shutter',
  'Toilet Door Shutter': 'Toilet Door Shutter',
  'Aluminium Sliding Doors & Window': 'Aluminium Sliding Doors & Window',
  'Aluminium Ventilator': 'Aluminium Ventilator',
  
  // Plaster
  'Back Coat Plaster': 'Back Coat Plaster',
  'Gypsum Plaster': 'Gypsum on Block work',
  'External Plaster': 'External Plaster -Double Coat',
  
  // Waterproofing
  'Chemical Waterproofing': 'Chemical Waterproofing in Toilet',
  'Toilet Waterproofing': 'Chemical Waterproofing in Toilet',
  'Terrace Waterproofing': 'Chemical Waterproofing in Terrace',
  'Utility Waterproofing': 'Chemical Waterproofing in Utility',
};

/**
 * Get manpower work type from task subcategory
 * Includes fuzzy matching for unmapped types
 */
export const getManpowerWorkType = (
  category: string, 
  subCategory: string,
  availableWorkTypes?: string[]
): string => {
  // Try exact match first
  if (WORK_TYPE_MAPPING[subCategory]) {
    return WORK_TYPE_MAPPING[subCategory];
  }

  // If available work types provided, try fuzzy match
  if (availableWorkTypes && availableWorkTypes.length > 0) {
    const lowerSub = subCategory.toLowerCase();
    
    // Try to find a work type that contains or is contained by subcategory
    const match = availableWorkTypes.find(wt => {
      const lowerWt = wt.toLowerCase();
      return lowerWt.includes(lowerSub) || lowerSub.includes(lowerWt);
    });

    if (match) return match;

    // Try word matching
    const subWords = lowerSub.split(/\s+/);
    const matchByWord = availableWorkTypes.find(wt => {
      const lowerWt = wt.toLowerCase();
      return subWords.some(word => lowerWt.includes(word) && word.length > 3);
    });

    if (matchByWord) return matchByWord;
  }

  // Fallback: return subcategory as-is
  console.warn(`No work type mapping found for: ${category} - ${subCategory}`);
  return subCategory;
};

/**
 * Get labor rate (with fallback to default)
 */
export const getLaborRate = (labour: string): number => {
  const stored = localStorage.getItem(`laborRate_${labour}`);
  if (stored) return parseFloat(stored);
  return DEFAULT_LABOR_RATES[labour] || 0;
};

/**
 * Get available workforce (with fallback to default)
 */
export const getAvailableWorkforce = (labour: string): number => {
  const stored = localStorage.getItem(`workforce_${labour}`);
  if (stored) return parseInt(stored, 10);
  return DEFAULT_WORKFORCE[labour] || 1;
};

/**
 * Update labor rate
 */
export const updateLaborRate = (labour: string, rate: number): void => {
  localStorage.setItem(`laborRate_${labour}`, rate.toString());
};

/**
 * Update available workforce
 */
export const updateAvailableWorkforce = (labour: string, count: number): void => {
  localStorage.setItem(`workforce_${labour}`, count.toString());
};

/**
 * Get all labor rates (merged with stored values)
 */
export const getAllLaborRates = (): Record<string, number> => {
  const rates = { ...DEFAULT_LABOR_RATES };
  Object.keys(rates).forEach(labour => {
    rates[labour] = getLaborRate(labour);
  });
  return rates;
};

/**
 * Get all workforce availability (merged with stored values)
 */
export const getAllWorkforce = (): Record<string, number> => {
  const workforce = { ...DEFAULT_WORKFORCE };
  Object.keys(workforce).forEach(labour => {
    workforce[labour] = getAvailableWorkforce(labour);
  });
  return workforce;
};

/**
 * Reset to defaults
 */
export const resetToDefaults = (): void => {
  Object.keys(DEFAULT_LABOR_RATES).forEach(labour => {
    localStorage.removeItem(`laborRate_${labour}`);
    localStorage.removeItem(`workforce_${labour}`);
  });
};

